import { useCallback, useState } from 'react';
import { Numbers, PushPin, PushPinOutlined } from '@mui/icons-material';
import { Fab, Tooltip } from '@mui/material';
import { ScalarBarChart, ScalarLineChart } from 'components/Charts';
import { REGULAR_UNIT, USD_MILLIONS, USD_THOUSANDS } from 'components/FeaturedSpreadsheet/constants';
import { ChartsProps } from './Charts.props';
import { ChartCard, DraggableChartGrid, Indicator } from './components';
import { ItemLayout } from './components/DraggableChartGrid/DraggableChartGrid.types';
import { getLineChartConfig, getMoicChartConfig } from './config';
import { useInvestedCapital, useMoic, useUnrealizedValue } from './hooks';

export const Charts = ({ selectedMeasurementDate }: ChartsProps) => {
  const [units, setUnits] = useState<string>(USD_MILLIONS);
  const {
    lineChartSeries: unrealizedValueSerires,
    indicatorValue: unrealizedValueIndicator,
    isLoading: isLoadingUnrealizedValue,
    isError: isErrorUnrealizedValue,
  } = useUnrealizedValue({ selectedMeasurementDate, units });

  const {
    lineChartSeries: moicSeries,
    indicatorValue: moicIndicator,
    isLoading: isLoadingMoic,
    isError: isErrorMoic,
  } = useMoic({ selectedMeasurementDate, units });

  const {
    lineChartSeries: investedCapitalSeries,
    indicatorValue: investedCapitalIndicator,
    isLoading: isLoadingInvestedCapital,
    isError: isErrorInvestedCapital,
  } = useInvestedCapital({ selectedMeasurementDate, units });

  const [allPinned, setAllPinned] = useState(true);
  const [layouts, setLayouts] = useState<ItemLayout[]>([]);

  const handleLayoutChange = useCallback((newLayouts: ItemLayout[]) => {
    setLayouts(newLayouts);
    const allPinnedState = newLayouts.length > 0 ? newLayouts.every(layout => layout.locked !== false) : true;
    setAllPinned(prev => (prev !== allPinnedState ? allPinnedState : prev));
  }, []);

  const handleToggleAllPinned = useCallback(() => {
    if (layouts.length === 0) {
      setAllPinned(prev => !prev);
      return;
    }

    setAllPinned(prev => {
      const newPinned = !prev;
      const updatedLayouts = layouts.map(layout => ({
        ...layout,
        locked: newPinned,
      }));
      setLayouts(updatedLayouts);
      return newPinned;
    });
  }, [layouts]);

  const handleToggleUnits = useCallback(() => {
    setUnits(prev => {
      if (prev === USD_MILLIONS) {
        return USD_THOUSANDS;
      }
      if (prev === USD_THOUSANDS) {
        return REGULAR_UNIT;
      }
      return USD_MILLIONS;
    });
  }, []);

  const getUnitLabel = useCallback((unit: string) => {
    if (unit === USD_THOUSANDS) {
      return 'Thousands';
    }
    if (unit === USD_MILLIONS) {
      return 'Millions';
    }
    return 'Regular';
  }, []);

  return (
    <>
      <DraggableChartGrid
        onLayoutChange={handleLayoutChange}
        layouts={layouts}
        cols={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 6 }}>
        <ChartCard key="unrealized-value" gridX={0} gridY={0} gridW={1} gridH={2}>
          <Indicator
            title="Unrealized Value"
            value={unrealizedValueIndicator.value}
            percentage={unrealizedValueIndicator.percentage}
            unit={units}
          />
          <ScalarLineChart
            series={unrealizedValueSerires}
            config={getLineChartConfig()}
            area
            showPoints
            smooth
            style={{ height: '100%', width: '100%' }}
            loading={isLoadingUnrealizedValue}
            error={isErrorUnrealizedValue ? new Error('Failed to load unrealized value data') : null}
          />
        </ChartCard>
        <ChartCard key="moic" gridX={0} gridY={2} gridW={1} gridH={2}>
          <Indicator title="MOIC" value={moicIndicator.value} />
          <ScalarBarChart
            series={moicSeries}
            config={getMoicChartConfig(moicSeries)}
            style={{ height: '100%', width: '100%' }}
            loading={isLoadingMoic}
            error={isErrorMoic ? new Error('Failed to load MOIC data') : null}
          />
        </ChartCard>
        <ChartCard key="invested-capital" gridX={0} gridY={2} gridW={1} gridH={2}>
          <Indicator
            title="Invested Capital"
            value={investedCapitalIndicator.value}
            percentage={investedCapitalIndicator.percentage}
            unit={units}
          />
          <ScalarLineChart
            series={investedCapitalSeries}
            config={getLineChartConfig()}
            area
            showPoints
            smooth
            style={{ height: '100%', width: '100%' }}
            loading={isLoadingInvestedCapital}
            error={isErrorInvestedCapital ? new Error('Failed to load invested capital data') : null}
          />
        </ChartCard>
      </DraggableChartGrid>
      <Tooltip title={allPinned ? 'Unpin all charts' : 'Pin all charts'} arrow>
        <Fab
          color="primary"
          aria-label={allPinned ? 'Unpin all charts' : 'Pin all charts'}
          onClick={handleToggleAllPinned}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            transform: allPinned ? 'none' : 'rotate(30deg)',
            transition: 'transform 0.3s ease',
          }}>
          {allPinned ? <PushPin /> : <PushPinOutlined />}
        </Fab>
      </Tooltip>
      <Tooltip title={`Switch units (Current: ${getUnitLabel(units)})`} arrow>
        <Fab
          color="secondary"
          aria-label={`Switch units (Current: ${getUnitLabel(units)})`}
          onClick={handleToggleUnits}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 88,
          }}>
          <Numbers />
        </Fab>
      </Tooltip>
    </>
  );
};
